import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const OTPInputField = ({ length = 6, value, onChange, disabled = false, error = false }) => {
  const [otp, setOtp] = useState(new Array(length)?.fill(''));
  const inputRefs = useRef([]);

  // Sync with parent value
  useEffect(() => {
    if (value !== undefined) {
      if (value?.length === 0) {
        // Clear all inputs when parent resets
        setOtp(new Array(length)?.fill(''));
      } else if (value?.length === length) {
        // Set individual digits when parent provides full value
        setOtp(value?.split(''));
      } else if (value?.length > 0 && value?.length < length) {
        // Handle partial input
        const newOtp = new Array(length)?.fill('');
        value?.split('')?.forEach((char, index) => {
          if (index < length && /^\d$/?.test(char)) {
            newOtp[index] = char;
          }
        });
        setOtp(newOtp);
      }
    }
  }, [value, length]);

  const handleChange = (element, index) => {
    const inputValue = element?.value;
    
    // Only allow single digits
    if (inputValue && !/^\d$/?.test(inputValue)) return false;

    const newOtp = [...otp];
    newOtp[index] = inputValue;
    setOtp(newOtp);

    // Create the complete OTP string
    const otpString = newOtp?.join('');
    console.log('OTP Input Change:', otpString, 'Array:', newOtp);
    
    // Call parent onChange with the complete string
    onChange?.(otpString);

    // Focus next input if current input has value
    if (inputValue && index < length - 1) {
      inputRefs?.current?.[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e?.key === 'Backspace') {
      const newOtp = [...otp];
      
      if (!otp?.[index] && index > 0) {
        // If current input is empty, move to previous and clear it
        newOtp[index - 1] = '';
        inputRefs?.current?.[index - 1]?.focus();
      } else {
        // Clear current input
        newOtp[index] = '';
      }
      
      setOtp(newOtp);
      const otpString = newOtp?.join('');
      console.log('OTP Backspace:', otpString, 'Array:', newOtp);
      onChange?.(otpString);
    }
  };

  const handlePaste = (e) => {
    e?.preventDefault();
    const pastedData = e?.clipboardData?.getData('text/plain')?.trim()?.slice(0, length);
    
    // Only allow numeric data
    if (!/^\d+$/?.test(pastedData)) {
      console.log('Invalid paste data:', pastedData);
      return;
    }

    const newOtp = new Array(length)?.fill('');
    pastedData?.split('')?.forEach((char, index) => {
      if (index < length) {
        newOtp[index] = char;
      }
    });
    
    setOtp(newOtp);
    const otpString = newOtp?.join('');
    console.log('OTP Paste:', otpString, 'Array:', newOtp);
    onChange?.(otpString);

    // Focus appropriate input after paste
    const lastFilledIndex = Math.min(pastedData?.length - 1, length - 1);
    const nextIndex = Math.min(pastedData?.length, length - 1);
    inputRefs?.current?.[nextIndex]?.focus();
  };

  const handleFocus = (index) => {
    // Select all text when input is focused (for better UX)
    inputRefs?.current?.[index]?.select();
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-center space-x-reverse space-x-3">
        {otp?.map((digit, index) => (
          <input
            key={index}
            ref={(ref) => (inputRefs.current[index] = ref)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e?.target, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            onFocus={() => handleFocus(index)}
            disabled={disabled}
            className={`w-12 h-12 text-center text-lg font-semibold border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
              error
                ? 'border-error text-error bg-error/5'
                : digit
                ? 'border-primary text-primary bg-primary/5' :'border-border text-foreground bg-input hover:border-primary/50'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'}`}
            dir="ltr"
            autoComplete="one-time-code"
          />
        ))}
      </div>
      {error && (
        <div className="flex items-center justify-center text-sm text-error">
          <Icon name="AlertCircle" size={16} className="ml-1" />
          کد وارد شده صحیح نمی‌باشد
        </div>
      )}
    </div>
  );
};

export default OTPInputField;